/**
 * Tooltip content for all form fields
 */

export const TOOLTIP_CONTENT = {
  // === WORKFORCE MODELLER - COHORT FIELDS ===

  employeeCount: "The number of employees in this cohort. Used to calculate total workforce SSP liability for this group.",

  contractedHours: "The minimum guaranteed hours per week. Affects Average Weekly Earnings (AWE) calculation and eligibility determination.",

  avgPickupHours: "Additional hours typically worked above contracted minimum. Common in hospitality where staff pick up extra shifts. These hours count toward AWE.",

  hourlyRate: "Gross hourly pay rate before deductions. Used to calculate Average Weekly Earnings (AWE) for eligibility and SSP rate determination.",

  qualifyingDays: (
    <>
      <strong>Days per week the employee normally works</strong> and can claim SSP for.
      For shift workers with variable patterns, this is often set to 7, with SSP only paid for rostered days during sickness.
      <br/><br/>
      Affects the <strong>daily SSP rate</strong> calculation: Weekly Rate ÷ Qualifying Days.
    </>
  ),

  typicalShiftLength: "Average hours per shift. Used to calculate backfill costs when covering absent employees.",

  rotaProbability: (
    <>
      The probability (0-1) that any given sick day falls on a <strong>rostered shift</strong>.
      <br/><br/>
      <strong>Example:</strong> Someone working 5 days/week has probability ~0.71 (5÷7).
      <br/><br/>
      Accounts for absences falling on non-working days, where no SSP or backfill cost applies.
    </>
  ),

  shiftsPerWeek: "Average number of shifts worked per week. Automatically converts to rota probability for calculations (shifts ÷ 7).",

  // === INDIVIDUAL CALCULATOR - EMPLOYEE DETAILS ===

  employeeId: "Optional identifier for record-keeping purposes. Not used in calculations.",

  // === INDIVIDUAL CALCULATOR - 8-WEEK EARNINGS ===

  eightWeekEarnings: (
    <>
      Enter the <strong>gross earnings</strong> for each of the 8 weeks <strong>before sickness began</strong>.
      <br/><br/>
      Used to calculate Average Weekly Earnings (AWE). Weeks with no work should be £0.
      <br/><br/>
      For monthly-paid employees, contact support for correct AWE calculation method.
    </>
  ),

  // === INDIVIDUAL CALCULATOR - ABSENCE DETAILS ===

  totalSickDays: (
    <>
      Total number of <strong>calendar days</strong> the employee was absent.
      <br/><br/>
      <strong>Current rules:</strong> Must be 4+ consecutive days to form a "Period of Incapacity for Work" (PIW) and qualify for SSP.
      <br/><br/>
      <strong>April 2026:</strong> Any absence qualifies - no minimum period required.
    </>
  ),

  rosteredDays: (
    <>
      The number of <strong>qualifying days</strong> during the absence where the employee was scheduled to work.
      <br/><br/>
      SSP is <strong>only payable for rostered qualifying days</strong>, not for scheduled days off or non-qualifying days.
    </>
  ),

  linkedPeriod: (
    <>
      Check this if the current sickness is within <strong>8 weeks (56 days)</strong> of a previous absence.
      <br/><br/>
      <strong>Effect:</strong> Waiting days won't apply again to linked periods. The two absences are treated as one continuous Period of Incapacity for Work.
    </>
  ),

  // === SICKNESS PATTERN ===

  avgEpisodesPerYear: (
    <>
      Average number of <strong>separate sickness absences</strong> per employee per year.
      <br/><br/>
      <strong>UK averages:</strong>
      <br/>• Hospitality: ~2.5 episodes
      <br/>• Retail: ~2.2 episodes
      <br/>• Care/Healthcare: ~3.0 episodes
    </>
  ),

  absenceDistribution: (
    <>
      Percentage breakdown of absence lengths. <strong>Critical for cost modelling</strong> as:
      <br/><br/>
      <strong>Current rules:</strong> 1-3 day absences receive no SSP (no PIW formed)
      <br/><br/>
      <strong>April 2026:</strong> All absences receive SSP from day 1
      <br/><br/>
      Short-term absences typically account for 60-75% of all episodes.
    </>
  ),

  avgAbsenceLength: "Average number of days for absences in this duration band. Used to calculate total sick days and SSP liability.",

  // === BACKFILL OPTIONS ===

  backfillMethod: (
    <>
      How absent employees are replaced:
      <br/><br/>
      <strong>Same rate:</strong> Existing staff at normal pay
      <br/><strong>Overtime:</strong> Existing staff at premium rate (typically 1.25×)
      <br/><strong>Agency:</strong> External agency staff at marked-up rate (typically 1.3×)
    </>
  ),

  overtimePremium: "Multiplier for overtime rates. Standard UK overtime is 1.25× (time and a quarter) or 1.5× (time and a half).",

  agencyMarkup: "Agency staff cost multiplier. Typical hospitality agency markup is 1.3× (30% extra) to 1.5× (50% extra).",
};
